import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mountain, Waves, Wind, Zap, Bike, Anchor } from "lucide-react";
import api from "@/lib/api";
import Shell from "@/components/Shell";
import ExperienceGrid from "@/components/ExperienceGrid";

const HERO = "https://images.pexels.com/photos/32651303/pexels-photo-32651303.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const IDEAS = [
  { icon: Waves, title: "White-Water Rafting", desc: "Grade IV Ganga rapids with certified guides." },
  { icon: Wind, title: "Paragliding", desc: "Soar tandem over Bir Billing's valleys." },
  { icon: Anchor, title: "Scuba Diving", desc: "Coral reefs of the Andaman Islands." },
  { icon: Mountain, title: "Skiing & Snow", desc: "Beginner slopes in Solang & Auli." },
  { icon: Zap, title: "Bungee Jumping", desc: "India's highest fixed-platform jump, Rishikesh." },
  { icon: Bike, title: "Himalayan Road Trips", desc: "Manali–Leh on two wheels or four." },
];

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.05 } }) };

export default function Adventure() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/experiences?category=adventure").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <Shell>
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="White-water rafting" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/30 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 md:py-36">
          <motion.span initial="hidden" animate="show" variants={fade} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white">
            <Zap size={14} /> Adventure Tourism
          </motion.span>
          <motion.h1 custom={1} initial="hidden" animate="show" variants={fade} className="font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-6 text-white max-w-3xl">
            For the ones who<br /><span className="italic font-normal">chase the rush.</span>
          </motion.h1>
          <motion.p custom={2} initial="hidden" animate="show" variants={fade} className="mt-6 text-lg font-light text-white/90 max-w-2xl leading-relaxed">
            Raft roaring rivers, fly over Himalayan valleys, dive living reefs and free-fall into gorges — all with certified guides and full safety gear.
          </motion.p>
        </div>
      </section>

      {/* IDEAS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Adventure ideas</span>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-2">Pick your adrenaline</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {IDEAS.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.div key={a.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                className="group rounded-2xl border border-primary/10 bg-card p-6 hover:-translate-y-1 transition-transform" data-testid={`idea-${i}`}>
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-accent/15 text-accent"><Icon size={22} /></span>
                <h3 className="font-semibold text-lg mt-4 text-foreground">{a.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{a.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* BOOKABLE ADVENTURES */}
      <section className="bg-secondary/40 border-y border-primary/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Book now</span>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-2">Adventures ready to go</h2>
            </div>
            <Link to="/explore?category=adventure" className="inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all" data-testid="adventure-all">All adventures <ArrowRight size={18} /></Link>
          </div>
          <div className="mt-10">
            {items.length ? <ExperienceGrid items={items} prefix="adventure-exp" /> : <p className="text-muted-foreground py-10">Loading adventures…</p>}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 pb-24">
        <div className="rounded-[2rem] bg-primary text-primary-foreground p-10 md:p-16">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight max-w-2xl">Turn a weekend into a story worth telling.</h2>
          <p className="mt-4 text-primary-foreground/70 text-lg max-w-lg">Book an adventure, then add flights, a stay and a cab — all in one place.</p>
          <Link to="/explore?category=adventure" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-4 font-semibold mt-8 hover:-translate-y-px transition-transform" data-testid="adventure-cta">
            Find your rush <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </Shell>
  );
}
